import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DroneManufacturer } from '../../core/data-models/drone-manufacturer.model';
import { DroneModel } from '../../core/data-models/drone-model.model';
import { DroneManufacturerService } from '../../core/data-services/drone-manufacturer.service';
import { DroneModelService } from '../../core/data-services/drone-model.service';

declare var bootstrap: any;

@Component({
  selector: 'app-manufacturer-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './manufacturer-detail.component.html',
  styleUrls: ['./manufacturer-detail.component.scss']
})
export class ManufacturerDetailComponent implements OnInit {
  manufacturerId!: string;
  manufacturer: DroneManufacturer | null = null;
  models: DroneModel[] = [];
  loading = true;
  loadingModels = false;
  errorMessage = '';
  archiving = false;
  archivingModelId: string | null = null;
  modelToArchive: DroneModel | null = null;
  private archiveModal: any;
  private archiveModelModal: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private droneManufacturerService: DroneManufacturerService,
    private droneModelService: DroneModelService
  ) {}

  ngOnInit(): void {
    this.manufacturerId = this.route.snapshot.params['id'];
    this.loadManufacturer();
  }

  async loadManufacturer(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      this.manufacturer = await firstValueFrom(this.droneManufacturerService.getManufacturer(this.manufacturerId));
      if (this.manufacturer) {
        this.loadModels();
      }
    } catch (error) {
      console.error('Failed to load manufacturer:', error);
      this.errorMessage = 'Failed to load manufacturer details';
    } finally {
      this.loading = false;
    }
  }

  async loadModels(): Promise<void> {
    this.loadingModels = true;

    try {
      this.models = await firstValueFrom(this.droneModelService.getModels(this.manufacturerId));
    } catch (error) {
      console.error('Failed to load models:', error);
      // Non-critical - don't show error to user
    } finally {
      this.loadingModels = false;
    }
  }

  formatDate(value: Date | string | null | undefined): string {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  formatWeight(weight: number | null | undefined): string {
    if (weight === null || weight === undefined) return '-';
    return `${weight.toLocaleString()} lbs`;
  }

  isOver55Lbs(weight: number | null | undefined): boolean {
    return weight !== null && weight !== undefined && weight > 55;
  }

  get modelsOver55LbsCount(): number {
    return this.models.filter(m => this.isOver55Lbs(m.maxTakeoffWeightLbs)).length;
  }

  goBack(): void {
    this.router.navigate(['/drone-catalog']);
  }

  openWebsite(): void {
    if (this.manufacturer?.url) {
      window.open(this.manufacturer.url, '_blank');
    }
  }

  editManufacturer(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'edit']);
  }

  addModel(): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'models', 'create']);
  }

  editModel(model: DroneModel): void {
    this.router.navigate(['/drone-catalog', this.manufacturerId, 'models', model.id, 'edit']);
  }

  confirmArchiveModel(model: DroneModel): void {
    this.modelToArchive = model;
    const modalElement = document.getElementById('archiveModelModal');
    if (modalElement) {
      this.archiveModelModal = new bootstrap.Modal(modalElement);
      this.archiveModelModal.show();
    }
  }

  async archiveModel(): Promise<void> {
    if (!this.modelToArchive) return;

    this.archivingModelId = this.modelToArchive.id;

    try {
      await firstValueFrom(this.droneModelService.archiveModel(this.modelToArchive.id));

      if (this.archiveModelModal) {
        this.archiveModelModal.hide();
      }

      // Remove from list and reload
      this.models = this.models.filter(m => m.id !== this.modelToArchive?.id);
      this.modelToArchive = null;
    } catch (error: any) {
      console.error('Failed to archive model:', error);
      this.errorMessage = 'Failed to archive model. Please try again.';
      if (this.archiveModelModal) {
        this.archiveModelModal.hide();
      }
    } finally {
      this.archivingModelId = null;
    }
  }

  confirmArchive(): void {
    const modalElement = document.getElementById('archiveModal');
    if (modalElement) {
      this.archiveModal = new bootstrap.Modal(modalElement);
      this.archiveModal.show();
    }
  }

  async archiveManufacturer(): Promise<void> {
    this.archiving = true;

    try {
      await firstValueFrom(this.droneManufacturerService.archiveManufacturer(this.manufacturerId));

      if (this.archiveModal) {
        this.archiveModal.hide();
      }
      this.router.navigate(['/drone-catalog']);
    } catch (error: any) {
      console.error('Failed to archive manufacturer:', error);
      this.errorMessage = 'Failed to archive manufacturer. Please try again.';
      if (this.archiveModal) {
        this.archiveModal.hide();
      }
    } finally {
      this.archiving = false;
    }
  }
}
